import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/design-system/ui/Button';
import { Input } from '@/design-system/ui/Field';
import { Sheet } from '@/design-system/ui/Sheet';
import { toast } from '@/design-system/ui/toast';
import { toEnglishDigits, toPersianDigits } from '@/core/format/numbers';
import { isValidMobile, normalizeMobile } from '@/core/validation/validators';
import { toApiError } from '@/core/api/errors';
import { authApi } from '../api/auth-api';
import { useAuthStore } from '../model/auth-store';

const RESEND_SECONDS = 120;
const CODE_LENGTH = 6;

function useCountdown(seconds: number) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return { left, start: () => setLeft(seconds) };
}

/** Global login dialog; opened from anywhere via `useRequireAuth()`. Mounted only while open, so its state resets for free. */
export function LoginSheet() {
  const loginOpen = useAuthStore((s) => s.loginOpen);
  return loginOpen ? <LoginFlow /> : null;
}

function LoginFlow() {
  const { closeLogin, setSession } = useAuthStore();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const countdown = useCountdown(RESEND_SECONDS);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const send = useMutation({
    mutationFn: (p: string) => authApi.sendCode(p),
    onSuccess: () => {
      setStep('code');
      setCode('');
      setError(undefined);
      countdown.start();
    },
    onError: (e) => setError(toApiError(e).message),
  });

  const verify = useMutation({
    mutationFn: ({ p, c }: { p: string; c: string }) => authApi.verifyCode(p, c),
    onSuccess: (token, { p }) => {
      setSession(token, p);
      // Read the continuation *before* closing, which clears it.
      const next = useAuthStore.getState().onLoggedIn;
      closeLogin();
      toast.success('با موفقیت وارد شدید');
      next?.();
    },
    onError: (e) => setError(toApiError(e).message || 'کد وارد شده نادرست است'),
  });

  const submitPhone = () => {
    const normalized = normalizeMobile(phone);
    if (!normalized || !isValidMobile(normalized)) return setError('شماره موبایل نامعتبر است (مثال: ۰۹۱۲۳۴۵۶۷۸۹)');
    setError(undefined);
    setPhone(normalized);
    send.mutate(normalized);
  };

  const submitCode = (value = code) => {
    if (value.length < CODE_LENGTH) return setError('کد تایید را کامل وارد کنید');
    setError(undefined);
    verify.mutate({ p: phone, c: value });
  };

  const mm = String(Math.floor(countdown.left / 60)).padStart(2, '0');
  const ss = String(countdown.left % 60).padStart(2, '0');

  return (
    <Sheet open onClose={closeLogin} title={step === 'phone' ? 'ورود یا ثبت‌نام' : 'کد تایید را وارد کنید'} dismissible={!verify.isPending}>
      {step === 'phone' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitPhone();
          }}
        >
          <p className="text-sm text-ink-2">برای ادامه، شماره موبایل خود را وارد کنید. کد تایید پیامک می‌شود.</p>
          <Input
            label="شماره موبایل"
            inputMode="numeric"
            autoComplete="tel"
            autoFocus
            dir="ltr"
            maxLength={14}
            placeholder="09123456789"
            value={phone}
            onChange={(e) => setPhone(toEnglishDigits(e.target.value))}
            error={error}
          />
          <Button type="submit" size="lg" fullWidth loading={send.isPending}>
            دریافت کد تایید
          </Button>
        </form>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitCode();
          }}
        >
          <p className="text-sm text-ink-2">
            کد ارسال‌شده به <b dir="ltr">{toPersianDigits(phone)}</b> را وارد کنید.
          </p>
          <Input
            ref={codeRef}
            label="کد تایید"
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            maxLength={CODE_LENGTH}
            className="text-center text-xl tracking-[0.5em]"
            value={code}
            onChange={(e) => {
              const v = toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, CODE_LENGTH);
              setCode(v);
              if (v.length === CODE_LENGTH) submitCode(v);
            }}
            error={error}
          />
          <Button type="submit" size="lg" fullWidth loading={verify.isPending}>
            ورود
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" className="font-bold text-blue" onClick={() => setStep('phone')}>
              ویرایش شماره
            </button>
            {countdown.left > 0 ? (
              <span className="text-ink-2" aria-live="off">
                ارسال مجدد تا {toPersianDigits(`${mm}:${ss}`)}
              </span>
            ) : (
              <button type="button" className="font-bold text-blue" onClick={() => send.mutate(phone)} disabled={send.isPending}>
                ارسال مجدد کد
              </button>
            )}
          </div>
        </form>
      )}
    </Sheet>
  );
}
